/**
 * Utility functions for handling image capture, processing and OCR
 * for the TradesPurple Quick Add feature
 *
 * All processing is done locally without storing images in the cloud
 *
 * Image processing pipeline for OCR optimization:
 * 1. Resize to a manageable size for better performance
 * 2. Apply slight blur to reduce noise (pin holes, paper texture)
 * 3. Convert to grayscale using standard luminance formula
 * 4. Apply thresholding to create high-contrast black and white
 * 5. Compress to JPEG for efficient processing
 *
 * These processing steps significantly improve OCR accuracy for
 * business cards and vehicle signage by reducing noise and
 * enhancing text visibility.
 */
import { debug } from '@/lib/utils';

/**
 * Detect if running on a mobile device or iOS Safari
 * @returns boolean indicating if the current browser is mobile or iOS Safari
 */
const isMobileOrIOSSafari = (): boolean => {
  const ua = navigator.userAgent;
  // Check for iOS Safari specifically
  const isIOSSafari = /iPad|iPhone|iPod/.test(ua) &&
         !window.MSStream &&
         /WebKit/.test(ua) &&
         !/Chrome/.test(ua);

  // Check for mobile devices more broadly
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(ua) ||
                   (window.innerWidth <= 768) ||
                   ('ontouchstart' in window);

  return isIOSSafari || isMobile;
};

/**
 * Detect if running specifically on iOS (iPhone/iPad)
 */
const isIOS = (): boolean => {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

// Import Tesseract.js dynamically and only on desktop devices
const loadTesseract = async () => {
  // Check if we're on mobile before even attempting to load Tesseract
  if (isMobileOrIOSSafari()) {
    debug.log('Mobile device detected - Tesseract.js not supported, skipping load');
    throw new Error('OCR is not supported on mobile devices');
  }

  try {
    debug.log('Loading Tesseract.js library for desktop...');
    const { createWorker } = await import('tesseract.js');
    debug.log('Tesseract.js library loaded, creating worker...');

    // Create worker with desktop-optimized configuration
    const worker = await createWorker({
      logger: (m) => {
        // Log Tesseract progress for debugging
        if (m.status) {
          debug.log(`Tesseract: ${m.status}`, m.progress);
        }
      },
      errorHandler: (err) => {
        debug.error('Tesseract worker error:', err);
      }
    });

    debug.log('Tesseract worker created successfully');
    return worker;
  } catch (error) {
    debug.error('Failed to load Tesseract.js:', error);
    throw new Error('Failed to load text recognition engine');
  }
};

/**
 * Simplified image processing for iOS Safari
 * Avoids canvas imageData manipulation which can cause issues on iOS
 *
 * @param file The image file to process
 * @param maxWidth Maximum width of the processed image
 * @returns A promise that resolves to a processed image Blob
 */
const compressImageIOSSafari = async (file: File, maxWidth = 1200): Promise<Blob> => {
  debug.log('Mobile image processing started', {
    fileSize: file.size,
    fileType: file.type,
    maxWidth
  });

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      const image = new Image();
      image.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = image.width;
        let height = image.height;

        debug.log('Original image dimensions', { width, height });

        // Balance between text detail and memory usage on mobile
        // Reduce from 1600 to 1200 for better memory management
        const mobileMaxWidth = Math.min(maxWidth, 1200);

        if (width > mobileMaxWidth) {
          const ratio = mobileMaxWidth / width;
          width = mobileMaxWidth;
          height = height * ratio;
        }

        debug.log('Final image dimensions', { width, height });

        // Create canvas and context for resizing
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        // Draw the image at the new size
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Enhance contrast for better OCR on mobile
        // Use sharpen filter for better text clarity
        ctx.filter = 'contrast(1.3) brightness(1.05)';
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(image, 0, 0, width, height);
        ctx.filter = 'none';

        // Convert to blob with higher quality for mobile OCR
        try {
          canvas.toBlob(
            (blob) => {
              if (blob) {
                debug.log('Mobile image processing completed', {
                  finalSize: blob.size,
                  finalDimensions: { width, height }
                });
                resolve(blob);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            0.85 // Balanced quality for OCR while reducing file size on mobile
          );
        } catch (error) {
          debug.error('Error converting canvas to blob:', error);
          // Fallback for older browsers or if toBlob fails
          try {
            const dataURL = canvas.toDataURL('image/jpeg', 0.92);
            fetch(dataURL)
              .then(res => res.blob())
              .then(blob => {
                debug.log('Image processing completed (fallback)', {
                  finalSize: blob.size
                });
                resolve(blob);
              })
              .catch(err => {
                debug.error('Fallback blob conversion failed:', err);
                reject(new Error('Failed to process image'));
              });
          } catch (fallbackError) {
            debug.error('All image conversion methods failed:', fallbackError);
            reject(new Error('Browser does not support image processing'));
          }
        }
      };

      image.onerror = () => {
        // Clean up resources on error
        image.src = '';
        reject(new Error('Failed to load image'));
      };

      // Set the source of the image to the reader result
      if (typeof readerEvent.target?.result === 'string') {
        image.src = readerEvent.target.result;
      } else {
        reject(new Error('Failed to read image'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Processes an image file to optimize it for OCR text extraction
 *
 * Processing steps:
 * - Resizes the image to manageable dimensions
 * - Applies a slight blur to reduce noise (pin holes, paper texture)
 * - Converts to grayscale using luminance formula
 * - Applies thresholding for high-contrast black & white
 * - Compresses to reduce file size
 *
 * @param file The image file to process
 * @param maxWidth Maximum width of the processed image
 * @returns A promise that resolves to a processed image Blob
 */
export const compressImage = async (file: File, maxWidth = 1200): Promise<Blob> => {
  // Use simplified processing for mobile devices
  if (isMobileOrIOSSafari()) {
    debug.log('Using mobile-compatible image processing');
    return compressImageIOSSafari(file, maxWidth);
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      const image = new Image();
      image.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = image.width;
        let height = image.height;

        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = height * ratio;
        }

        // Create canvas and context for resizing
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        // Draw the image at the new size
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        try {
          // Step 1: Apply a slight blur to reduce noise (pin holes, paper texture)
          // Save the original state
          ctx.save();

          // Apply blur filter
          ctx.filter = 'blur(1px)';
          ctx.drawImage(image, 0, 0, width, height);

          // Reset filter and restore context state
          ctx.filter = 'none';
          ctx.restore();

          // Step 2: Convert to grayscale
          const imageData = ctx.getImageData(0, 0, width, height);
          const data = imageData.data;

          for (let i = 0; i < data.length; i += 4) {
            // Use standard luminance formula to convert to grayscale
            const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            data[i] = data[i + 1] = data[i + 2] = gray;
          }

          // Step 3: Apply thresholding to create high contrast black and white
          // This helps distinguish text from background noise
          const threshold = 128; // Middle gray value

          for (let i = 0; i < data.length; i += 4) {
            // Convert to either pure black or pure white based on threshold
            const value = data[i] > threshold ? 255 : 0;
            data[i] = data[i+1] = data[i+2] = value;
            // Alpha channel remains unchanged
          }

          ctx.putImageData(imageData, 0, 0);
        } catch (error) {
          debug.error('Error processing image pixels:', error);
          // If pixel manipulation fails, redraw the image without processing
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(image, 0, 0, width, height);
        }

        // Convert to blob with reasonable quality
        try {
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            0.85 // 85% quality provides good balance of quality vs file size
          );
        } catch (error) {
          debug.error('Error converting canvas to blob:', error);
          // Fallback for older browsers or if toBlob fails
          try {
            const dataURL = canvas.toDataURL('image/jpeg', 0.92);
            fetch(dataURL)
              .then(res => res.blob())
              .then(blob => {
                debug.log('Image processing completed (fallback)', {
                  finalSize: blob.size
                });
                resolve(blob);
              })
              .catch(err => {
                debug.error('Fallback blob conversion failed:', err);
                reject(new Error('Failed to process image'));
              });
          } catch (fallbackError) {
            debug.error('All image conversion methods failed:', fallbackError);
            reject(new Error('Browser does not support image processing'));
          }
        }
      };

      image.onerror = () => {
        // Clean up resources on error
        image.src = '';
        reject(new Error('Failed to load image'));
      };

      // Set the source of the image to the reader result
      if (typeof readerEvent.target?.result === 'string') {
        image.src = readerEvent.target.result;
      } else {
        reject(new Error('Failed to read image'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Create a promise that rejects after a specified timeout
 * @param ms The timeout in milliseconds
 * @returns A promise that rejects after the timeout
 */
const timeout = (ms: number): Promise<never> => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Operation timed out after ${ms}ms`)), ms);
  });
};

/**
 * Run a promise with a timeout
 * @param promise The promise to run
 * @param ms The timeout in milliseconds
 * @returns A promise that resolves with the result or rejects with a timeout error
 */
const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
  return Promise.race([promise, timeout(ms)]);
};

/**
 * Extracts text from an image using OCR
 * @param imageFile The image file to process
 * @param timeoutMs Optional timeout in milliseconds (default: 20000ms)
 * @returns A promise that resolves to the extracted text
 */
export const performOCR = async (imageFile: File, timeoutMs = 12000): Promise<string> => {
  const isMobile = isMobileOrIOSSafari();
  const isIOSDevice = isIOS();

  // For mobile devices, return empty string and let user enter manually
  // OCR is not reliable on mobile due to memory/WebAssembly issues and large bundle size
  if (isMobile) {
    debug.log('Mobile device detected - skipping OCR, user will enter details manually');
    return '';
  }

  try {
    debug.log('Starting OCR processing on desktop', {
      fileName: imageFile.name,
      fileSize: imageFile.size,
      fileType: imageFile.type,
      timeoutMs
    });

    // Compress the image for better performance
    debug.log('Starting image compression');
    let processedImage: Blob;
    try {
      processedImage = await withTimeout(compressImage(imageFile), 10000);
      debug.log('Image compression completed', {
        originalSize: imageFile.size,
        compressedSize: processedImage.size,
        compressionRatio: (imageFile.size / processedImage.size).toFixed(2)
      });
    } catch (compressionError) {
      debug.error('Image compression failed:', compressionError);
      // Try to use original image if compression fails
      processedImage = imageFile;
      debug.log('Using original image due to compression failure');
    }

    debug.log('Loading Tesseract.js worker');
    // Load Tesseract.js worker
    const worker = await withTimeout(loadTesseract(), 10000);

    debug.log('Initializing OCR engine');
    // Initialize the worker with English language
    await withTimeout(worker.loadLanguage('eng'), 10000);
    await withTimeout(worker.initialize('eng'), 10000);

    debug.log('OCR processing started for image');

    // Set parameters for better results with business cards/van signs
    await worker.setParameters({
      tessedit_ocr_engine_mode: 3, // Legacy + LSTM mode for better results
      preserve_interword_spaces: 1, // Preserve spaces
    });

    // Recognize text in the image
    debug.log('Running OCR recognition');
    const { data } = await withTimeout(worker.recognize(processedImage), timeoutMs);

    // Terminate the worker to free memory
    await worker.terminate();

    debug.log('OCR processing completed', {
      extractedTextLength: data.text?.length || 0,
      confidence: data.confidence,
      textPreview: data.text?.substring(0, 100) || 'No text extracted'
    });

    return data.text;
  } catch (error) {
    debug.error('OCR processing error:', error);
    debug.error('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });

    // Add specific error message for timeout
    if (error.message && error.message.includes('timed out')) {
      throw new Error('OCR processing took too long. Please try again or enter details manually.');
    }

    // Check for common desktop browser issues
    if (error.message && error.message.includes('WebAssembly')) {
      throw new Error('OCR is not supported in this browser. Please enter details manually.');
    }

    if (error.message && error.message.includes('Worker')) {
      throw new Error('Could not initialize OCR worker. Please enter details manually.');
    }

    if (error.message && error.message.includes('OCR is not supported on mobile devices')) {
      throw new Error('OCR is not available on mobile devices. Please enter details manually.');
    }

    throw new Error('Failed to extract text from image. Please enter details manually.');
  }
};

// Upload functionality removed - all processing is now local
